import { type JSX, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Group,
  Modal,
  Paper,
  Stack,
  TagsInput,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { type Capacidades, type TagNaLista, api, explicarEnvio } from '../lib/api.js';

/**
 * "Pôr na lista de avisos": cria contato a partir de uma CONVERSA.
 *
 * É a outra metade do problema dos contatos fora do SAGA. Quem já escreveu
 * para o aeroclube tem telefone conhecido e CONFIRMADO — ele mandou mensagem
 * de verdade, não foi digitado por alguém numa planilha —, e estava fora de
 * qualquer lista só por não ter cadastro. É o contato de melhor qualidade que
 * entra no sistema.
 *
 * ENDEREÇADA PELO ATENDIMENTO, nunca pelo telefone. O servidor resolve o
 * número a partir dele, como em todo o resto do painel: mandar o JID daqui
 * tornaria decorativa a máscara que a lista aplica (ver a nota em
 * `rotas-conversas.ts`).
 *
 * O contato nasce com `origem = 'conversa'`, e é essa coluna que o protege da
 * sincronização do SAGA — que desativa quem não está na listagem do sistema
 * acadêmico, e um interessado nunca está.
 */
export function MarcarNaLista({
  atendimentoId,
  nomeSugerido,
  capacidades,
}: {
  atendimentoId: string;
  /** Nome que a conversa já conhece. Vira a sugestão do campo. */
  nomeSugerido?: string | null;
  capacidades: Capacidades;
}): JSX.Element | null {
  const [aberto, setAberto] = useState(false);
  const [nome, setNome] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [sugestoes, setSugestoes] = useState<string[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!aberto) return;
    setNome(nomeSugerido ?? '');
    setTags([]);
    setErro(null);
    void api
      .get<{ itens: TagNaLista[] }>('/admin/tags')
      .then((r) => setSugestoes(r.itens.map((t) => t.nome)))
      .catch(() => undefined);
  }, [aberto, nomeSugerido]);

  /**
   * A saída antecipada vem DEPOIS dos hooks, e não antes.
   *
   * Hook atrás de `if` muda a ordem de chamada entre renders e o React
   * quebra — `capacidades` pode chegar depois, e o componente passaria de
   * "zero hooks" para "um hook" no meio da vida dele.
   */
  if (!capacidades.disparar) return null;

  async function salvar(): Promise<void> {
    setSalvando(true);
    try {
      const r = await api.post<{ criado: boolean }>('/admin/contatos/de-conversa', {
        atendimento: atendimentoId,
        ...(nome.trim() ? { nome: nome.trim() } : {}),
        ...(tags.length > 0 ? { tags } : {}),
      });
      notifications.show({
        message: r.criado
          ? 'Contato criado e marcado. Já entra nas listas de envio.'
          : 'Este telefone já tinha cadastro — as tags foram acrescentadas.',
        color: 'green',
      });
      setAberto(false);
    } catch (e) {
      setErro(explicarEnvio(e, 'falha ao criar o contato'));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <>
      <Paper withBorder p="xs" radius="md" mb="xs">
        <Group justify="space-between" gap="xs" wrap="nowrap">
          <Text size="xs" c="dimmed">
            Esta pessoa pode entrar nas listas de aviso — o telefone dela já está confirmado.
          </Text>
          <Button size="xs" variant="default" onClick={() => setAberto(true)}>
            Pôr na lista de avisos
          </Button>
        </Group>
      </Paper>

      <Modal
        opened={aberto}
        onClose={() => setAberto(false)}
        title="Pôr na lista de avisos"
        centered
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Cria um cadastro a partir desta conversa. O telefone vem do próprio atendimento — não
            há o que digitar. Se já houver cadastro com este número, nada é duplicado: só as tags
            são acrescentadas.
          </Text>

          <TextInput
            label="Nome"
            description="Opcional. Fica como o nome do cadastro, e é o que a variável {nome} usa."
            value={nome}
            onChange={(e) => setNome(e.currentTarget.value)}
          />

          <TagsInput
            label="Tags"
            description="Escreva e aperte Enter. Tag que ainda não existe é criada."
            data={sugestoes}
            value={tags}
            onChange={setTags}
            clearable
          />

          {erro && (
            <Alert color="red" variant="light" role="alert">
              <Text size="sm">{erro}</Text>
            </Alert>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAberto(false)}>
              Cancelar
            </Button>
            <Button loading={salvando} onClick={() => void salvar()}>
              Salvar
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
